import React, { useState } from "react";
import { AiOutlineClose } from "react-icons/ai";
import { apiClient } from "../../../api";
import { toast } from "react-toastify";
import { useProperty } from "../../../Contexts/PropertyContext/PropertyContext";
import { Loader2, QrCode, CheckCircle2, AlertCircle } from "lucide-react";

const AddQRModal = ({ onClose, propertyDetails }) => {
    const { selectedPropertyId } = useProperty();
    const [codeArray, setCodeArray] = useState(new Array(7).fill(""));
    const [isLinking, setIsLinking] = useState(false);
    const [hasLinkedAnyQR, setHasLinkedAnyQR] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");

    const shortCode = codeArray.join("").trim();

    const handleClose = () => {
        onClose();
        if (hasLinkedAnyQR && window.location.pathname.includes("qr-management")) {
            window.location.reload();
        }
    };

    const handleChange = (e, index) => {
        const val = e.target.value.toUpperCase();
        // Allow only alphanumeric characters (A-Z, 0-9)
        if (val && !/^[A-Z0-9]$/.test(val)) return;

        const newArray = [...codeArray];
        newArray[index] = val;
        setCodeArray(newArray);

        // Auto-focus the next input field
        if (val && index < 6) {
            const nextInput = document.getElementById(`code-input-${index + 1}`);
            if (nextInput) {
                nextInput.focus();
                // Select text for easier rewriting
                setTimeout(() => nextInput.select(), 10);
            }
        }
    };

    const handleKeyDown = (e, index) => {
        if (e.key === "Backspace") {
            const newArray = [...codeArray];
            
            if (codeArray[index]) {
                // If current slot has a character, just delete it
                newArray[index] = "";
                setCodeArray(newArray);
            } else if (index > 0) {
                // If current slot is empty, move back and delete previous character
                newArray[index - 1] = "";
                setCodeArray(newArray);
                const prevInput = document.getElementById(`code-input-${index - 1}`);
                if (prevInput) {
                    prevInput.focus();
                }
            }
        } else if (e.key === "ArrowLeft" && index > 0) {
            document.getElementById(`code-input-${index - 1}`)?.focus();
        } else if (e.key === "ArrowRight" && index < 6) {
            document.getElementById(`code-input-${index + 1}`)?.focus();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        // Get pasted text and normalize it
        const pastedData = e.clipboardData
            .getData("text")
            .toUpperCase()
            .trim()
            .replace(/[^A-Z0-9]/g, "");

        if (pastedData.length > 0) {
            const newArray = [...codeArray];
            // Fill up to 7 characters from the pasted string
            const fillLength = Math.min(pastedData.length, 7);
            for (let i = 0; i < fillLength; i++) {
                newArray[i] = pastedData[i];
            }
            setCodeArray(newArray);

            // Focus the appropriate input after paste
            const focusIndex = Math.min(fillLength, 6);
            const targetInput = document.getElementById(`code-input-${focusIndex}`);
            if (targetInput) {
                targetInput.focus();
            }
        }
    };

    const handleLinkQR = async (e) => {
        if (e) e.preventDefault();
        
        if (shortCode.length !== 7) {
            toast.error("Please enter the complete 7-character shortcode.");
            return;
        }

        setIsLinking(true);
        try {
            const response = await apiClient.post("/api/qrs/claim", {
                shortCode: shortCode,
                propertyId: selectedPropertyId || propertyDetails?._id,
            });

            if (response.status === 200) {
                const serverMsg = response.data.message || "QR Code linked successfully!";
                toast.success(serverMsg);
                setSuccessMessage(serverMsg);
                setHasLinkedAnyQR(true);
                setCodeArray(new Array(7).fill("")); // Reset code inputs
            }
        } catch (err) {
            const errorMsg = err.response?.data?.message || "Failed to link QR code.";
            toast.error(errorMsg);
        } finally {
            setIsLinking(false);
        }
    };

    return (
        <div
            className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4 backdrop-blur-sm"
            onClick={handleClose}
        >
            <div
                className="bg-white rounded-[2rem] shadow-2xl max-w-md w-full relative animate-in fade-in zoom-in duration-300 overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header Section */}
                <div className="bg-gradient-to-br from-indigo-600 to-indigo-800 p-8 text-white relative">
                    <button
                        onClick={handleClose}
                        className="absolute top-6 right-6 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-all active:scale-90"
                    >
                        <AiOutlineClose className="h-5 w-5 text-white" />
                    </button>
                    
                    <div className="w-14 h-14 bg-white/15 rounded-2xl flex items-center justify-center mb-5 backdrop-blur-md border border-white/10 shadow-inner">
                        <QrCode className="w-7 h-7 text-white" />
                    </div>
                    
                    <h2 className="text-2xl font-bold tracking-tight">Add QR Stand</h2>
                    <p className="mt-2 text-indigo-100/90 text-sm leading-relaxed">
                        To pair this property with a physical QR stand, enter the unique shortcode printed on it.
                    </p>
                </div>

                {/* Body Section */}
                <div className="p-8">
                    {successMessage ? (
                        <div className="text-center py-4 animate-in fade-in slide-in-from-bottom-4 duration-300">
                            <div className="w-16 h-16 bg-green-50 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-green-100">
                                <CheckCircle2 className="w-8 h-8 text-green-600 animate-bounce" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-1">Successfully Linked!</h3>
                            <p className="text-sm text-gray-500 mb-6 font-medium">Your QR stand has been paired with this property.</p>
                            
                            <div className="flex gap-3">
                                <button
                                    onClick={() => setSuccessMessage("")}
                                    className="flex-1 py-3 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-bold rounded-xl transition-all text-xs active:scale-95"
                                >
                                    Add Another Code
                                </button>
                                <button
                                    onClick={handleClose}
                                    className="flex-1 py-3 bg-gray-900 hover:bg-gray-800 text-white font-bold rounded-xl transition-all text-xs active:scale-95 shadow-lg shadow-gray-200"
                                >
                                    Done
                                </button>
                            </div>
                        </div>
                    ) : (
                        <form onSubmit={handleLinkQR} className="space-y-6">
                            <div className="space-y-3">
                                <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1">
                                    Enter 7-Char Shortcode
                                </label>
                                
                                {/* 7-Character input slots */}
                                <div className="flex justify-between gap-1.5 sm:gap-2.5" onPaste={handlePaste}>
                                    {codeArray.map((char, index) => (
                                        <input
                                            key={index}
                                            id={`code-input-${index}`}
                                            type="text"
                                            value={char}
                                            onChange={(e) => handleChange(e, index)}
                                            onKeyDown={(e) => handleKeyDown(e, index)}
                                            maxLength={1}
                                            autoFocus={index === 0}
                                            className="w-10 h-12 sm:w-11 sm:h-14 bg-gray-50 border-2 border-gray-150 focus:border-indigo-500 focus:bg-white focus:ring-4 focus:ring-indigo-100/60 rounded-xl text-lg sm:text-xl font-black text-center uppercase transition-all outline-none"
                                        />
                                    ))}
                                </div>

                                <div className="flex items-center gap-1.5 ml-1 text-gray-400">
                                    <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                                    <span className="text-[10px] font-semibold tracking-wide leading-relaxed">
                                        Enter the shortcode printed on the front of the stand (e.g. ABC1234).
                                    </span>
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={isLinking || shortCode.length !== 7}
                                className="w-full flex items-center justify-center gap-2 py-4 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-2xl transition-all shadow-lg shadow-indigo-100 active:scale-[0.98] text-sm disabled:bg-gray-100 disabled:text-gray-400 disabled:shadow-none disabled:cursor-not-allowed border border-indigo-650 disabled:border-transparent"
                            >
                                {isLinking ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        <span>Linking Stand...</span>
                                    </>
                                ) : (
                                    <span>Link QR Code</span>
                                )}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AddQRModal;
